import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import './SignupPage.css';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';


export default function SignupPage() {
  const API_URL = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost') || window.location.hostname === '127.0.0.1'
    ? `http://${window.location.hostname}:5000`
    : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');

  const location = useLocation();
  const navigate = useNavigate();
  const { login, signup } = useAuth();
  const { t } = useLanguage();

  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

  // Determine mode: signup is default
  const isLoginMode = searchParams.get('mode') === 'login';
  const [mode, setMode] = useState(isLoginMode ? 'login' : 'signup');

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    phone: ""
  });
  const [agreeTerms, setAgreeTerms] = useState(true);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authSuccess, setAuthSuccess] = useState("");

  // Sync mode with URL if it changes
  useEffect(() => {
    const urlMode = searchParams.get('mode');
    if (urlMode === 'login') setMode('login');
    else if (urlMode === 'signup') setMode('signup');
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (authError) setAuthError('');
  };

  const validateForm = () => {
    const newErrors = {};
    if (mode === "signup") {
      if (!formData.fullName.trim()) newErrors.fullName = t('nameRequired') || "Name is required";
      if (formData.phone.trim() && !/^\d{10}$/.test(formData.phone.trim())) {
        newErrors.phone = t('phoneInvalid') || "Phone number must be 10 digits";
      }
      if (!agreeTerms) newErrors.terms = "You must agree to the terms";
    }

    if (!formData.email.trim()) newErrors.email = t('emailRequired') || "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t('emailInvalid') || "Email is invalid";

    if (!formData.password) {
      newErrors.password = t('passwordRequired') || "Password is required";
    } else if (mode === "signup" && formData.password.length < 8) {
      newErrors.password = t('passwordMinLength') || "Password must be at least 8 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAuthError("");
    setAuthSuccess("");

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      let result;
      if (mode === "signup") {
        result = await signup(formData.fullName, formData.email, formData.password, formData.phone, 'admin');
      } else {
        result = await login(formData.email, formData.password);
      }

      if (!result.success) {
        setAuthError(result.message || "Authentication failed");
        setIsLoading(false);
        return;
      }

      setAuthSuccess("Redirecting...");
      const { user, company } = result;
      const token = result.user.token;

      // Store token in localStorage before redirect (security best practice)
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(result.user));

      if (mode === "signup" || (user?.role === 'admin' || user?.role_id)) {
        if (company && company.slug) {
          const currentHostname = window.location.hostname;
          const isLocalhostEnv = currentHostname.includes('localhost') || currentHostname === '127.0.0.1';
          const port = window.location.port ? `:${window.location.port}` : '';

          if (isLocalhostEnv) {
            // We are in a localhost environment (e.g. localhost, 127.0.0.1, or raj.localhost)
            const targetHostname = `${company.slug}.localhost`;

            // If we are ALREADY on the correct subdomain (e.g. raj.localhost), 
            // just go to the homepage path (token already in localStorage)
            if (currentHostname === targetHostname) {
              window.location.href = `/homepage`;
            } else {
              // Otherwise, redirect to the correct subdomain.
              // Pass token in URL so AuthContext can save it to the new subdomain's localStorage
              // (subdomains have separate localStorage, so the token must be re-seeded via URL)
              window.location.href = `http://${targetHostname}${port}/homepage?token=${token}`;
            }
          } else {
            // Production logic: Trust the company.url
            // Pass token in URL so AuthContext can save it to the tenant domain's localStorage
            const baseUrl = company.url || '/homepage';
            const separator = baseUrl.includes('?') ? '&' : '?';
            window.location.href = `${baseUrl}${separator}token=${token}`;
          }
        } else {
          navigate('/homepage');
        }
      } else {
        navigate('/customer.html');
      }
    } catch (error) {
      setAuthError(error.message || "An unexpected error occurred.");
      setIsLoading(false);
    }
  };

  const toggleMode = (newMode) => {
    setMode(newMode);
    setAuthError("");
    setAuthSuccess("");
    setErrors({});
    // Update URL without refreshing to keep state in sync
    const params = new URLSearchParams(window.location.search);
    params.set('mode', newMode);
    window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  return (
    <div className="global-auth-page">
      <div className="global-auth-content">

        {/* LEFT SIDE: THE PREVIOUS SLIDING CARD */}
        <div className="auth-card-side">
          <div className={`auth-card-container ${mode === 'login' ? 'login-active' : 'signup-active'}`}>

            {/* 1. LOGIN FORM PANEL */}
            <div className="form-panel sign-in-panel">
              <form className="auth-form" onSubmit={handleSubmit}>
                <h2 className="form-title">Welcome Back</h2>
                <p className="form-subtitle">Login to manage your restaurant dashboard</p>

                <div className={`input-group ${errors.email ? 'has-error' : ''}`}>
                  <label>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>

                <div className={`input-group ${errors.password ? 'has-error' : ''}`}>
                  <div className="label-flex">
                    <label>Password</label>
                    <span className="forgot-link blue-link" onClick={() => setShowForgotPassword(true)}>Forgot?</span>
                  </div>
                  <div className="password-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Min 8 characters"
                      value={formData.password}
                      onChange={handleInputChange}
                    />
                    <button type="button" className="eye-btn" onClick={() => setShowPassword(!showPassword)}>
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                  {errors.password && <span className="error-text">{errors.password}</span>}
                </div>

                {mode === 'login' && authError && <div className="error-alert">{authError}</div>}

                <button type="submit" className="submit-btn primary-btn" disabled={isLoading}>
                  {isLoading ? <div className="spinner"></div> : 'Sign In'}
                </button>

                <p className="footer-text">
                  First time here? <span className="text-link highlight-link" onClick={() => toggleMode('signup')}>Create Account</span>
                </p>
              </form>
            </div>

            {/* 2. SIGN UP FORM PANEL */}
            <div className="form-panel sign-up-panel">
              <form className="auth-form" onSubmit={handleSubmit}>
                <h2 className="form-title">Get Started Now</h2>
                <p className="form-subtitle">Setup your restaurant admin account today</p>

                <div className={`input-group ${errors.fullName ? 'has-error' : ''}`}>
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Enter your name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                  />
                  {errors.fullName && <span className="error-text">{errors.fullName}</span>}
                </div>

                <div className={`input-group ${errors.phone ? 'has-error' : ''}`}>
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="10-digit number"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                  {errors.phone && <span className="error-text">{errors.phone}</span>}
                </div>

                <div className={`input-group ${errors.email ? 'has-error' : ''}`}>
                  <label>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>

                <div className={`input-group ${errors.password ? 'has-error' : ''}`}>
                  <label>Password</label>
                  <div className="password-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Min 8 characters"
                      value={formData.password}
                      onChange={handleInputChange}
                    />
                    <button type="button" className="eye-btn" onClick={() => setShowPassword(!showPassword)}>
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                  {errors.password && <span className="error-text">{errors.password}</span>}
                </div>

                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="terms-check"
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                  />
                  <label htmlFor="terms-check">I agree to the <a href="/terms-of-service">Terms & Policy</a></label>
                </div>

                {mode === 'signup' && authError && <div className="error-alert">{authError}</div>}
                {authSuccess && <div className="success-alert">{authSuccess}</div>}

                <button type="submit" className="submit-btn primary-btn" disabled={isLoading}>
                  {isLoading ? <div className="spinner"></div> : 'Sign Up'}
                </button>

                <p className="footer-text">
                  Have an account? <span className="text-link highlight-link" onClick={() => toggleMode('login')}>Sign In</span>
                </p>
              </form>
            </div>

            {/* 3. INTERNAL SLIDING OVERLAY */}
            <div className="sliding-overlay-container">
              <div className="sliding-overlay">
                <div className="overlay-content">
                  <img
                    src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1920&auto=format&fit=crop"
                    alt="Trendy Restaurant Food"
                    className="stationary-img"
                  />
                  <div className="overlay-text-center">
                    <div style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      background: '#fff',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                      padding: '8px',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                    }}>
                      <img src="/app-logo.png" alt="RedSorm" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </div>
                    <h3 className="overlay-brand">RedSorm</h3>
                    <p className="overlay-tagline">Where Flavor Meets Innovation</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <div className="global-image-side">
          <div className="image-collage-wrapper">
            <img
              src="/signup-bg-premium.png"
              alt="Elite Dining Ecosystem"
              className="collage-image-main"
            />
            <div className="collage-overlay">
              <h2 className="premium-header">Master Your Kitchen</h2>
              <p className="premium-tagline">Precision management for the modern restaurateur.</p>
            </div>
          </div>
        </div>

      </div>

      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        API_URL={API_URL}
      />

    </div>
  );
}
